import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSector } from '../../contexts/SectorContext';
import {
  createSector,
  formatWhatsAppQrSrc,
  getWhatsAppQRCode,
  getWhatsAppStatus,
} from '../../services/SectorService';
import {
  fetchMicrosoftOAuthAuthorizeUrlAndRedirect,
  fetchOAuthAuthorizeUrlAndRedirect,
  getEmailConnectionStatus,
} from '../../services/EmailConnectionService';
import { fetchCalendarAuthorizeUrlAndRedirect, getCalendarConnectionStatus } from '../../services/CalendarService';
import { sparkConnectionErrorMessage, startSparkConnectionSession } from '../../services/SparkConnectionSessionService';
import {
  getMetaConnectionStatus,
  type MetaConnectionStatus,
  type WhatsAppCompleteResult,
} from '../../services/MetaConnectionService';
import WhatsAppSignupButton from '../../components/meta/WhatsAppSignupButton';
import { isWhatsAppCloudConfigured } from '../../components/meta/WhatsAppCloudCard';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import {
  BaseBuildDiscoveries,
  BaseBuildJob,
  BaseBuildUndoResult,
  cancelMemoryBackfill,
  confirmBaseBuild,
  confirmMemoryBackfill,
  estimateBaseBuild,
  estimateMemoryBackfill,
  fetchActiveMemoryBackfill,
  fetchBaseBuild,
  fetchDiscoveries,
  fetchLatestBaseBuild,
  fetchMemoryBackfill,
  fetchOnboardingStatus,
  forgetOnboardingReturn,
  isActiveJob,
  keptReasonText,
  MemoryBackfillJob,
  noteText,
  oauthErrorText,
  OnboardingSector,
  pauseBaseBuild,
  rememberOnboardingReturn,
  reportOnboardingStep,
  resumeBaseBuild,
  STEP_LABELS,
  undoBaseBuild,
} from '../services/onboarding';
import '../styles/mendsAi.css';
import '../styles/onboarding.css';

type ChannelState = 'checking' | 'connected' | 'not_connected' | 'unknown';

const STEP_ORDER = ['contacts', 'history', 'cards', 'funnel', 'insights'];
const WINDOWS = [30, 90, 180];
const MEMORY_WINDOWS = [7, 30, 90];

function fmt(n?: number | null) {
  return (n ?? 0).toLocaleString('pt-BR');
}

function money(value?: number | null, currency?: string | null) {
  if (value == null) return null;
  const code = currency === 'USD' || currency === 'EUR' || currency === 'BRL' ? currency : 'BRL';
  try {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: code, maximumFractionDigits: value % 1 === 0 ? 0 : 2 });
  } catch {
    return value.toLocaleString('pt-BR');
  }
}

function statusText(job: BaseBuildJob) {
  switch (job.status) {
    case 'running': return 'Montando a base…';
    case 'paused': return 'Pausado';
    case 'done': return 'Base montada';
    case 'failed': return 'Parou com erro';
    case 'undoing': return 'Desfazendo…';
    case 'undone': return 'Importação desfeita';
    default: return 'Aguardando confirmação';
  }
}

function stepPercent(job: BaseBuildJob) {
  const parts = STEP_ORDER.map((s) => job.steps?.[s]).filter(Boolean);
  if (parts.length === 0) return 0;
  const sum = parts.reduce((acc, p) => {
    if (!p) return acc;
    if (p.state === 'done' || p.state === 'skipped') return acc + 1;
    if (p.state === 'running' && p.total > 0) return acc + Math.min(1, p.done / p.total);
    return acc;
  }, 0);
  return Math.round((sum / STEP_ORDER.length) * 100);
}

/** Etapa do assistente com número, estado e conteúdo. */
const WizardStep: React.FC<{
  index: number;
  title: string;
  done: boolean;
  optional?: boolean;
  children: React.ReactNode;
}> = ({ index, title, done, optional, children }) => (
  <section className={`mai-card onb-step${done ? ' is-done' : ''}`} aria-label={title}>
    <header className="onb-step-head">
      <span className="onb-step-num" aria-hidden>{done ? '✓' : index}</span>
      <h3 className="mai-card-title">{title}</h3>
      {optional && !done && <span className="mai-pill">opcional</span>}
      {done && <span className="mai-pill mai-pill-ai">pronto</span>}
    </header>
    <div className="onb-step-body">{children}</div>
  </section>
);

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedSectorId, sectors, selectSector, refreshSectors } = useSector();
  const sector = useMemo(() => sectors.find((s) => s.id === selectedSectorId) ?? null, [sectors, selectedSectorId]);

  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<OnboardingSector[]>([]);

  const [whatsApp, setWhatsApp] = useState<ChannelState>('checking');
  const [email, setEmail] = useState<ChannelState>('checking');
  const [calendar, setCalendar] = useState<ChannelState>('checking');
  const [calendarNeedsReconnect, setCalendarNeedsReconnect] = useState(false);
  const [skipped, setSkipped] = useState<Record<string, boolean>>({});

  const [qr, setQr] = useState<string | null>(null);
  const [qrBusy, setQrBusy] = useState(false);
  const [metaBusy, setMetaBusy] = useState(false);
  /** Status da Meta da conexão escolhida (oficial): undefined = consultando · null = a consulta falhou. */
  const [metaStatus, setMetaStatus] = useState<MetaConnectionStatus | null | undefined>(undefined);

  const [newName, setNewName] = useState('');
  const [newOfficial, setNewOfficial] = useState(false);
  const [creating, setCreating] = useState(false);

  const [windowDays, setWindowDays] = useState(90);
  const [useTemplate, setUseTemplate] = useState(true);
  const [job, setJob] = useState<BaseBuildJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [discoveries, setDiscoveries] = useState<BaseBuildDiscoveries | null>(null);
  const [tab, setTab] = useState<'clients' | 'negotiations' | 'conversations'>('negotiations');
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);
  const reported = useRef<Record<string, boolean>>({});
  // Conexão escolhida agora: respostas que chegam depois de trocar de conexão são descartadas (nunca mostram o job,
  // o progresso ou as descobertas de outra conexão).
  const sectorRef = useRef<number | null>(selectedSectorId);
  sectorRef.current = selectedSectorId;
  const isCurrent = (sectorId: number | null | undefined) => sectorId != null && sectorRef.current === sectorId;

  const report = useCallback((step: 'whatsapp' | 'email' | 'calendar', detail?: string) => {
    if (!selectedSectorId || reported.current[`${selectedSectorId}:${step}`]) return;
    reported.current[`${selectedSectorId}:${step}`] = true;
    void reportOnboardingStep(step, selectedSectorId, detail);
  }, [selectedSectorId]);

  // Volta do Google/Microsoft/Meta: o backend redireciona para /sectors, que devolve para cá com os parâmetros.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    forgetOnboardingReturn();
    if (q.get('oauth_success')) {
      const provider = q.get('oauth_provider');
      setNotice(provider === 'google_calendar' ? 'Agenda conectada.' : 'E-mail conectado.');
    } else if (q.get('oauth_error')) {
      setError(oauthErrorText(q.get('oauth_error')));
    } else if (q.get('sparkSession')) {
      const status = q.get('sparkStatus');
      if (status === 'completed') setNotice('WhatsApp oficial conectado.');
      else if (status) setError('A conexão com a Meta não terminou. Tente de novo.');
    }
    if (q.toString().length > 0) navigate('/comecar', { replace: true });
    // só na chegada
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOverview = useCallback(async () => {
    try {
      const status = await fetchOnboardingStatus();
      setOverview(status.sectors);
    } catch {
      setOverview([]);
    }
  }, []);

  useEffect(() => { void loadOverview(); }, [loadOverview]);

  // Sem conexão escolhida, o assistente abre na que mais precisa: ligada sem base, depois sem base, depois a primeira.
  useEffect(() => {
    if (selectedSectorId || sectors.length === 0) return;
    const pick = overview.find((o) => o.whatsAppConnected && !o.baseReady)
      ?? overview.find((o) => !o.baseReady)
      ?? overview[0];
    const id = pick && sectors.some((s) => s.id === pick.sectorId) ? pick.sectorId : sectors[0].id;
    selectSector(id);
  }, [selectedSectorId, sectors, overview, selectSector]);

  // Estado dos três canais da conexão escolhida. Resposta de uma conexão que já não está escolhida é ignorada.
  useEffect(() => {
    const sectorId = selectedSectorId;
    if (!sectorId) return undefined;
    let alive = true;
    const keep = (fn: () => void) => { if (alive && isCurrent(sectorId)) fn(); };
    setWhatsApp('checking');
    setEmail('checking');
    setCalendar('checking');
    if (sector?.isOfficial) {
      // Conexão oficial: a conta da Meta (Cloud API direta) ou, enquanto não migrou, a linha do Spark.
      const sparkOn = !!sector.sparkIsActive;
      setMetaStatus(undefined);
      getMetaConnectionStatus(sectorId)
        .then((s) => keep(() => {
          setMetaStatus(s);
          const cloudOn = s.accounts.some((a) => a.channel === 'whatsapp' && a.status === 'active');
          setWhatsApp(cloudOn || sparkOn ? 'connected' : 'not_connected');
        }))
        .catch(() => keep(() => {
          setMetaStatus(null);
          setWhatsApp(sparkOn ? 'connected' : 'not_connected');
        }));
    } else {
      setMetaStatus(undefined);
      getWhatsAppStatus(sectorId, false)
        .then((s) => keep(() => setWhatsApp(s.status === 'connected' || s.connected ? 'connected' : 'not_connected')))
        .catch(() => keep(() => setWhatsApp('unknown')));
    }
    getEmailConnectionStatus(sectorId)
      .then((s) => keep(() => setEmail(s.hasConnection && s.status !== 'disconnected' ? 'connected' : 'not_connected')))
      .catch(() => keep(() => setEmail('unknown')));
    getCalendarConnectionStatus(sectorId)
      .then((s) => keep(() => {
        setCalendarNeedsReconnect(s.needsReconnect === true);
        setCalendar(s.hasConnection && s.status !== 'disconnected' ? 'connected' : 'not_connected');
      }))
      .catch(() => keep(() => setCalendar('unknown')));
    return () => { alive = false; };
    // isCurrent lê o ref; a conexão e o tipo dela definem a consulta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSectorId, sector?.isOfficial, sector?.sparkIsActive]);

  useEffect(() => { if (whatsApp === 'connected') report('whatsapp', sector?.isOfficial ? 'oficial' : 'qr'); }, [whatsApp, report, sector]);
  useEffect(() => { if (email === 'connected') report('email'); }, [email, report]);
  useEffect(() => { if (calendar === 'connected') report('calendar'); }, [calendar, report]);

  // Enquanto o QR está na tela, confere a conexão a cada 3 s.
  useEffect(() => {
    const sectorId = selectedSectorId;
    if (!qr || !sectorId || whatsApp === 'connected') return undefined;
    let alive = true;
    const id = window.setInterval(() => {
      getWhatsAppStatus(sectorId, false)
        .then((s) => {
          if (!alive || !isCurrent(sectorId)) return;
          if (s.status === 'connected' || s.connected) {
            setWhatsApp('connected');
            setQr(null);
            setNotice('WhatsApp conectado.');
            void loadOverview();
          }
        })
        .catch(() => undefined);
    }, 3000);
    return () => { alive = false; window.clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qr, selectedSectorId, whatsApp, loadOverview]);

  // Trocou de conexão: o QR, os avisos e o diálogo de desfazer eram da anterior.
  useEffect(() => {
    setQr(null);
    setConfirmUndo(false);
    setUndoError(null);
  }, [selectedSectorId]);

  /** Job vindo do servidor só entra na tela se ainda é da conexão escolhida. */
  const acceptJob = useCallback((next: BaseBuildJob | null, sectorId: number) => {
    if (!isCurrent(sectorId)) return;
    if (next && next.sectorId !== sectorId) return;
    setJob(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Último "montar a base" da conexão.
  useEffect(() => {
    setJob(null);
    setDiscoveries(null);
    const sectorId = selectedSectorId;
    if (!sectorId) return undefined;
    let alive = true;
    fetchLatestBaseBuild(sectorId)
      .then((j) => { if (alive) acceptJob(j, sectorId); })
      .catch(() => { if (alive) acceptJob(null, sectorId); });
    return () => { alive = false; };
  }, [selectedSectorId, acceptJob]);

  // Progresso ao vivo.
  useEffect(() => {
    if (!job || !isActiveJob(job)) return undefined;
    const { id: polledId, sectorId } = job;
    let alive = true;
    const id = window.setInterval(() => {
      fetchBaseBuild(polledId)
        .then((j) => { if (alive) acceptJob(j, sectorId); })
        .catch(() => undefined);
    }, 3000);
    return () => { alive = false; window.clearInterval(id); };
  }, [job, acceptJob]);

  const jobId = job?.id;
  const jobStatus = job?.status;
  const jobSectorId = job?.sectorId;
  useEffect(() => {
    if (!jobId || !jobSectorId || !(jobStatus === 'done' || jobStatus === 'failed' || jobStatus === 'paused')) return undefined;
    let alive = true;
    fetchDiscoveries(jobId)
      .then((d) => { if (alive && isCurrent(jobSectorId)) setDiscoveries(d); })
      .catch(() => { if (alive && isCurrent(jobSectorId)) setDiscoveries(null); });
    if (jobStatus === 'done') void loadOverview();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, jobStatus, jobSectorId, loadOverview]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(apiErrorMessage(e, 'Não foi possível concluir agora. Tente de novo.'));
    } finally {
      setBusy(false);
    }
  };

  const showQr = () => {
    const sectorId = selectedSectorId;
    if (!sectorId) return;
    setQrBusy(true);
    setError(null);
    getWhatsAppQRCode(sectorId, false)
      .then((r) => {
        if (!isCurrent(sectorId)) return;
        if (r.qrcode) setQr(formatWhatsAppQrSrc(r.qrcode));
        else setError(r.message || 'O QR code ainda está sendo gerado. Tente de novo em alguns segundos.');
      })
      .catch((e) => setError(apiErrorMessage(e, 'Não foi possível gerar o QR code.')))
      .finally(() => setQrBusy(false));
  };

  const connectMeta = async () => {
    if (!sector) return;
    setMetaBusy(true);
    setError(null);
    try {
      const session = await startSparkConnectionSession({ connectionName: sector.name, sectorId: sector.id });
      if (!session.url) throw new Error(session.userMessage || 'A Meta não devolveu o link de conexão.');
      rememberOnboardingReturn();
      window.location.assign(session.url);
    } catch (e) {
      setError(sparkConnectionErrorMessage(e, 'Não foi possível iniciar a conexão com a Meta.'));
      setMetaBusy(false);
    }
  };

  /** O Embedded Signup terminou e o servidor ligou a conexão ao WhatsApp oficial: o passo está feito. */
  const whatsAppConnected = (result: WhatsAppCompleteResult) => {
    setError(null);
    setMetaStatus(result.status);
    setWhatsApp('connected');
    setNotice(
      result.isOnBizApp
        ? 'WhatsApp oficial conectado. As conversas dos últimos 6 meses estão sendo importadas.'
        : 'WhatsApp oficial conectado.',
    );
    void refreshSectors();
    void loadOverview();
  };

  const createConnection = () => run(async () => {
    const name = newName.trim();
    if (!name) {
      setError('Dê um nome para a conexão (ex.: Comercial).');
      return;
    }
    setCreating(true);
    try {
      const created = await createSector({ name, description: name, phoneNumberId: '', accessToken: '', isOfficial: newOfficial });
      await refreshSectors();
      const id = created?.data?.id ?? created?.id;
      if (typeof id === 'number') selectSector(id);
      setNewName('');
      void loadOverview();
    } finally {
      setCreating(false);
    }
  });

  const oauth = (fn: (sectorId: number) => Promise<void>) => run(async () => {
    if (!selectedSectorId) return;
    rememberOnboardingReturn();
    await fn(selectedSectorId);
  });

  const estimate = () => run(async () => {
    const sectorId = selectedSectorId;
    if (!sectorId) return;
    setDiscoveries(null);
    acceptJob(await estimateBaseBuild(sectorId, windowDays), sectorId);
  });

  const confirm = () => run(async () => {
    if (!job) return;
    acceptJob(await confirmBaseBuild(job.id, useTemplate), job.sectorId);
  });

  const togglePause = () => run(async () => {
    if (!job) return;
    acceptJob(job.status === 'paused' ? await resumeBaseBuild(job.id) : await pauseBaseBuild(job.id), job.sectorId);
  });

  // Erro do desfazer aparece dentro do diálogo (o aviso da página fica escondido atrás dele).
  const undo = async () => {
    if (!job) return;
    const { id, sectorId } = job;
    setBusy(true);
    setUndoError(null);
    try {
      const r = await undoBaseBuild(id);
      if (!isCurrent(sectorId)) return;
      setConfirmUndo(false);
      setDiscoveries(null);
      setError(null);
      setNotice(undoNotice(r));
      acceptJob(await fetchBaseBuild(id), sectorId);
      void loadOverview();
    } catch (e) {
      if (isCurrent(sectorId)) setUndoError(apiErrorMessage(e, 'Não foi possível desfazer agora. Tente de novo em alguns segundos.'));
    } finally {
      setBusy(false);
    }
  };

  const whatsAppDone = whatsApp === 'connected';
  const cloudReady = isWhatsAppCloudConfigured(metaStatus);
  const cloudAccount = metaStatus?.accounts.find((a) => a.channel === 'whatsapp' && a.status === 'active') ?? null;
  const emailDone = email === 'connected' || !!skipped.email;
  const calendarDone = calendar === 'connected' || !!skipped.calendar;
  const baseDone = job?.status === 'done';
  const estimateView = job?.estimate;

  if (sectors.length === 0) {
    return (
      <div className="mai-page mai-comecar">
        <OnboardingHeader />
        {error && <p className="mai-form-error" role="alert">{error}</p>}
        <WizardStep index={1} title="Crie a sua conexão" done={false}>
          <p className="mai-card-detail">Uma conexão é um número de WhatsApp da empresa (por exemplo, o do Comercial).</p>
          <NewConnectionForm name={newName} official={newOfficial} busy={creating}
            onName={setNewName} onOfficial={setNewOfficial} onCreate={createConnection} />
        </WizardStep>
      </div>
    );
  }

  return (
    <div className="mai-page mai-comecar">
      <OnboardingHeader />
      {notice && <p className="onb-notice" role="status">{notice}</p>}
      {error && <p className="mai-form-error" role="alert">{error}</p>}

      <div className="onb-connection">
        <label htmlFor="onb-sector">Conexão</label>
        <select id="onb-sector" value={selectedSectorId ?? ''} onChange={(e) => selectSector(Number(e.target.value))}>
          {!selectedSectorId && <option value="">Escolha a conexão</option>}
          {sectors.map((s) => {
            const o = overview.find((x) => x.sectorId === s.id);
            return (
              <option key={s.id} value={s.id}>
                {s.name} · {s.isOfficial ? 'oficial' : 'QR code'}{o?.baseReady ? ' · base montada' : ''}
              </option>
            );
          })}
        </select>
      </div>

      <WizardStep index={1} title="Conecte o WhatsApp" done={whatsAppDone}>
        {whatsApp === 'checking' && <p className="mai-card-detail">Conferindo a conexão…</p>}
        {whatsAppDone && (
          <p className="mai-card-detail">
            WhatsApp conectado{cloudAccount ? ` · ${cloudAccount.displayPhone || cloudAccount.name || cloudAccount.accountId}` : sector?.isOfficial && sector.sparkLinePhone ? ` · linha ${sector.sparkLinePhone}` : ''}.
          </p>
        )}
        {whatsAppDone && cloudReady && !cloudAccount && sector?.isOfficial && sector.sparkIsActive != null && sector.id ? (
          <>
            <p className="mai-card-detail">Ao migrar, a conexão anterior do WhatsApp deste setor é desligada e tudo passa a ir direto pela Meta.</p>
            <WhatsAppSignupButton
              className="mai-btn"
              sectorId={sector.id}
              config={metaStatus!.whatsAppSignup!}
              label="Migrar para a conexão direta com a Meta"
              onConnected={whatsAppConnected}
              onError={setError}
            />
          </>
        ) : null}
        {!whatsAppDone && whatsApp !== 'checking' && !sector?.isOfficial && (
          <>
            <p className="mai-card-detail">
              No celular, abra o WhatsApp → <strong>Aparelhos conectados</strong> → <strong>Conectar um aparelho</strong> e escaneie o código.
            </p>
            {qr ? (
              <div className="onb-qr">
                <img src={qr} alt="QR code para conectar o WhatsApp" />
                <span className="mai-card-detail">Aguardando a leitura… a tela avança sozinha.</span>
              </div>
            ) : (
              <button type="button" className="mai-btn" onClick={showQr} disabled={qrBusy}>
                {qrBusy ? 'Gerando o QR code…' : 'Mostrar QR code'}
              </button>
            )}
          </>
        )}
        {!whatsAppDone && whatsApp !== 'checking' && sector?.isOfficial && cloudReady && sector.id && (
          <>
            <p className="mai-card-detail">WhatsApp oficial: o cadastro abre numa janela da Meta e a conexão termina aqui, sem sair da página.</p>
            <WhatsAppSignupButton
              className="mai-btn"
              sectorId={sector.id}
              config={metaStatus!.whatsAppSignup!}
              label={sector.sparkIsActive != null ? 'Migrar para a conexão direta com a Meta' : 'Conectar WhatsApp oficial (Meta)'}
              onConnected={whatsAppConnected}
              onError={setError}
            />
          </>
        )}
        {!whatsAppDone && whatsApp !== 'checking' && sector?.isOfficial && !cloudReady && (
          <>
            <p className="mai-card-detail">WhatsApp oficial: a conexão é feita na Meta e você volta para cá ao terminar.</p>
            <button type="button" className="mai-btn" onClick={connectMeta} disabled={metaBusy}>
              {metaBusy ? 'Abrindo a Meta…' : 'Conectar pela Meta'}
            </button>
          </>
        )}
        <details className="onb-more">
          <summary>Outra conexão</summary>
          <NewConnectionForm name={newName} official={newOfficial} busy={creating}
            onName={setNewName} onOfficial={setNewOfficial} onCreate={createConnection} />
        </details>
      </WizardStep>

      <WizardStep index={2} title="Conecte o e-mail" done={emailDone} optional>
        {email === 'connected' ? (
          <p className="mai-card-detail">E-mail conectado.</p>
        ) : (
          <div className="mai-btn-row">
            <button type="button" className="mai-btn mai-btn-soft" disabled={busy} onClick={() => oauth(fetchOAuthAuthorizeUrlAndRedirect)}>Gmail</button>
            <button type="button" className="mai-btn mai-btn-soft" disabled={busy} onClick={() => oauth(fetchMicrosoftOAuthAuthorizeUrlAndRedirect)}>Microsoft (Outlook)</button>
            <button type="button" className="mai-btn mai-btn-ghost" onClick={() => navigate('/sectors')}>Outro (SMTP)</button>
            {!skipped.email && <button type="button" className="mai-btn mai-btn-ghost" onClick={() => setSkipped((s) => ({ ...s, email: true }))}>Agora não</button>}
          </div>
        )}
      </WizardStep>

      <WizardStep index={3} title="Conecte a agenda" done={calendarDone} optional>
        {calendar === 'connected' ? (
          <p className="mai-card-detail">Agenda conectada.</p>
        ) : (
          <div className="mai-btn-row">
            {calendarNeedsReconnect && (
              <p className="mai-card-detail">O Google recusou o acesso à agenda (permissão revogada ou expirada). Conecte de novo para voltar a sincronizar.</p>
            )}
            <button type="button" className="mai-btn mai-btn-soft" disabled={busy} onClick={() => oauth(fetchCalendarAuthorizeUrlAndRedirect)}>
              {calendarNeedsReconnect ? 'Reconectar Google Agenda' : 'Google Agenda'}
            </button>
            {!skipped.calendar && <button type="button" className="mai-btn mai-btn-ghost" onClick={() => setSkipped((s) => ({ ...s, calendar: true }))}>Agora não</button>}
          </div>
        )}
      </WizardStep>

      <WizardStep index={4} title="Monte a base" done={baseDone}>
        <p className="mai-card-detail">
          A MENDS lê o histórico, cria os clientes e os cards, coloca cada um na coluna certa do funil e resume cada conversa.
          Nada é enviado aos seus clientes.
        </p>
        {(!job || job.status === 'undone' || job.status === 'discarded') && (
          <div className="onb-row">
            <label htmlFor="onb-window">Período do histórico</label>
            <select id="onb-window" value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))}>
              {WINDOWS.map((d) => <option key={d} value={d}>Últimos {d} dias</option>)}
            </select>
            <button type="button" className="mai-btn" onClick={estimate} disabled={busy || !selectedSectorId}>
              {busy ? 'Calculando…' : 'Calcular antes de começar'}
            </button>
          </div>
        )}

        {job?.status === 'estimated' && estimateView && (
          <div className="onb-estimate" aria-label="Estimativa">
            <div className="mai-kpi-strip">
              <Kpi label="Conversas no histórico" value={fmt(estimateView.chats)} />
              <Kpi label="Mensagens (estimativa)" value={`~${fmt(estimateView.estimatedMessages)}`} />
              <Kpi label="Clientes já na MENDS" value={fmt(estimateView.existingContacts)} />
              <Kpi label="Conversas a analisar" value={fmt(estimateView.projectedConversations)} />
              <Kpi label="Chamadas de IA (no máximo)" value={`até ${fmt(estimateView.aiCalls)}`} />
              <Kpi label="Tempo estimado" value={`~${fmt(estimateView.minutes)} min`} />
            </div>
            {noteText(estimateView.historyNote) && <p className="mai-card-detail onb-note">{noteText(estimateView.historyNote)}</p>}
            {!estimateView.funnelReady && (
              <label className="onb-check">
                <input type="checkbox" checked={useTemplate} onChange={(e) => setUseTemplate(e.target.checked)} />
                Usar o funil de vendas sugerido (Atendimento, Qualificado, Reunião agendada, Proposta, Venda fechada, Perdido).
                Sem ele, os cards ficam na primeira coluna.
              </label>
            )}
            <div className="mai-btn-row">
              <button type="button" className="mai-btn" onClick={confirm} disabled={busy}>Montar a base</button>
              <button type="button" className="mai-btn mai-btn-ghost" onClick={estimate} disabled={busy}>Recalcular</button>
            </div>
          </div>
        )}

        {job && job.status !== 'estimated' && job.status !== 'discarded' && (
          <div className="onb-progress" aria-label="Progresso">
            <div className="onb-progress-head">
              <strong>{statusText(job)}</strong>
              {job.status !== 'undone' && <span className="mai-card-detail">{stepPercent(job)}%</span>}
            </div>
            {job.status !== 'undone' && (
              <div className="onb-bar" role="progressbar" aria-valuenow={stepPercent(job)} aria-valuemin={0} aria-valuemax={100}>
                <span style={{ width: `${stepPercent(job)}%` }} />
              </div>
            )}
            <ul className="onb-steps">
              {STEP_ORDER.map((s) => {
                const p = job.steps?.[s];
                if (!p) return null;
                const note = noteText(p.note);
                return (
                  <li key={s} className={`is-${p.state}`}>
                    <span className="onb-steps-label">{STEP_LABELS[s]}</span>
                    <span className="mai-card-detail">
                      {p.state === 'pending' && 'na fila'}
                      {p.state === 'running' && (p.total > 0 ? `${fmt(p.done)} de ${fmt(p.total)}` : 'em andamento')}
                      {p.state === 'done' && (p.total > 0 ? `${fmt(p.done)} feitos` : 'feito')}
                      {p.state === 'skipped' && 'pulado'}
                      {p.state === 'failed' && 'falhou'}
                    </span>
                    {note && <span className="mai-card-detail onb-note">{note}</span>}
                  </li>
                );
              })}
            </ul>
            {job.status !== 'undone' && (
              <p className="mai-card-detail">
                {fmt(job.counts?.message)} mensagens importadas · {fmt(job.counts?.contact)} clientes novos · {fmt(job.counts?.card)} cards ·
                {' '}{fmt(job.counts?.move)} posicionados no funil · {fmt(job.counts?.summary)} resumos · {fmt(job.counts?.opportunity)} negociações com valor
              </p>
            )}
            <div className="mai-btn-row">
              {(job.status === 'running' || job.status === 'paused') && (
                <button type="button" className="mai-btn mai-btn-soft" onClick={togglePause} disabled={busy}>
                  {job.status === 'paused' ? 'Continuar' : 'Pausar'}
                </button>
              )}
              {(job.status === 'done' || job.status === 'failed' || job.status === 'paused') && (
                <button type="button" className="mai-btn mai-btn-danger" onClick={() => setConfirmUndo(true)} disabled={busy}>
                  Desfazer importação
                </button>
              )}
              {(job.status === 'undone' || job.status === 'failed') && (
                <button type="button" className="mai-btn mai-btn-ghost" onClick={estimate} disabled={busy}>Montar de novo</button>
              )}
              {job.status === 'done' && (
                <button type="button" className="mai-btn" onClick={() => navigate('/hoje')}>Ir para Hoje</button>
              )}
            </div>
          </div>
        )}
      </WizardStep>

      {discoveries && (
        <section className="mai-card onb-found" aria-label="O que a MENDS descobriu">
          <h3 className="mai-card-title">O que a MENDS descobriu</h3>
          <p className="mai-card-detail">Confira cada item: o link abre a conversa ou o card para você ver a evidência.</p>
          <div className="onb-tabs" role="tablist">
            <TabButton active={tab === 'negotiations'} onClick={() => setTab('negotiations')} label={`Negociações (${discoveries.negotiations.length})`} />
            <TabButton active={tab === 'clients'} onClick={() => setTab('clients')} label={`Clientes (${discoveries.clients.length})`} />
            <TabButton active={tab === 'conversations'} onClick={() => setTab('conversations')} label={`Conversas (${discoveries.conversations.length})`} />
          </div>

          {tab === 'negotiations' && (
            discoveries.negotiations.length === 0 ? (
              <p className="mai-empty-state">Nenhuma negociação encontrada nas conversas.</p>
            ) : (
              <ul className="onb-list">
                {discoveries.negotiations.map((n) => (
                  <li key={`${n.cardId}-${n.source}`}>
                    <div className="onb-list-main">
                      <strong>{n.name}</strong>
                      <span className="mai-card-detail">
                        {n.fromColumn && n.toColumn ? `${n.fromColumn} → ${n.toColumn}` : n.toColumn}
                        {n.title ? ` · ${n.title}` : ''}
                        {money(n.value, n.currency) ? ` · ${money(n.value, n.currency)}` : ''}
                      </span>
                      {n.evidence && <q className="onb-evidence">{n.evidence}</q>}
                    </div>
                    <div className="mai-btn-row">
                      <button type="button" className="mai-btn mai-btn-ghost" onClick={() => navigate(`/chat?contactId=${n.contactId}`)}>Abrir conversa</button>
                      <button type="button" className="mai-btn mai-btn-ghost" onClick={() => navigate('/crm')}>Ver no funil</button>
                    </div>
                  </li>
                ))}
              </ul>
            )
          )}

          {tab === 'clients' && (
            discoveries.clients.length === 0 ? (
              <p className="mai-empty-state">Nenhum cliente com conversa no período escolhido.</p>
            ) : (
            <ul className="onb-list">
              {discoveries.clients.map((c) => (
                <li key={c.contactId}>
                  <div className="onb-list-main">
                    <strong>{c.name}</strong>
                    <span className="mai-card-detail">
                      {c.createdByImport ? 'novo' : 'já existia'}
                      {c.phoneMasked ? ` · ${c.phoneMasked}` : ''}
                      {c.columnName ? ` · ${c.columnName}` : ''}
                      {c.messagesImported > 0 ? ` · ${fmt(c.messagesImported)} mensagens importadas` : ''}
                    </span>
                    {c.summary && <span className="onb-summary">{c.summary}</span>}
                    {c.lastCustomerMessage && <q className="onb-evidence">{c.lastCustomerMessage}</q>}
                  </div>
                  <div className="mai-btn-row">
                    <button type="button" className="mai-btn mai-btn-ghost" onClick={() => navigate(`/chat?contactId=${c.contactId}`)}>Abrir conversa</button>
                  </div>
                </li>
              ))}
            </ul>
            )
          )}

          {tab === 'conversations' && (
            discoveries.conversations.length === 0 ? (
              <p className="mai-empty-state">Nenhuma conversa nova foi importada (os clientes e as mensagens já estavam na MENDS).</p>
            ) : (
              <ul className="onb-list">
                {discoveries.conversations.map((c) => (
                  <li key={c.contactId}>
                    <div className="onb-list-main">
                      <strong>{c.name}</strong>
                      <span className="mai-card-detail">{fmt(c.messagesImported)} mensagens importadas</span>
                      {noteText(c.note) && <span className="mai-card-detail onb-note">{noteText(c.note)}</span>}
                    </div>
                    <div className="mai-btn-row">
                      <button type="button" className="mai-btn mai-btn-ghost" onClick={() => navigate(`/chat?contactId=${c.contactId}`)}>Abrir conversa</button>
                    </div>
                  </li>
                ))}
              </ul>
            )
          )}
        </section>
      )}

      {selectedSectorId && <MemoryBackfillBlock sectorId={selectedSectorId} index={5} />}
      <UndoDialog
        open={confirmUndo}
        connectionName={sector?.name ?? overview.find((o) => o.sectorId === selectedSectorId)?.name ?? null}
        busy={busy}
        error={undoError}
        onConfirm={undo}
        onCancel={() => { setConfirmUndo(false); setUndoError(null); }}
      />
    </div>
  );
};

function undoNotice(r: BaseBuildUndoResult) {
  const base = `Importação desfeita: ${fmt(r.messagesRemoved)} mensagens, ${fmt(r.contactsRemoved)} contatos e ${fmt(r.cardsRemoved)} cards removidos; ${fmt(r.movesReverted)} cards voltaram de coluna.`;
  const cards = r.cardsKept ?? 0;
  const contacts = r.contactsKept ?? 0;
  if (cards + contacts === 0) return base;
  const reasons = Array.from(new Set((r.kept ?? []).map((k) => keptReasonText(k.reason)))).slice(0, 3);
  return `${base} Ficaram ${fmt(cards)} cards e ${fmt(contacts)} clientes que foram usados depois${reasons.length ? ` (${reasons.join('; ')})` : ''}.`;
}

/** Confirmação do desfazer com o nome da conexão e o erro (ex.: "tente em alguns segundos") dentro do diálogo. */
const UndoDialog: React.FC<{
  open: boolean;
  connectionName: string | null;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, connectionName, busy, error, onConfirm, onCancel }) => {
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const t = window.setTimeout(() => confirmRef.current?.focus(), 40);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onCancel]);
  if (!open) return null;
  const title = connectionName ? `Desfazer a importação da conexão “${connectionName}”?` : 'Desfazer a importação desta conexão?';
  return (
    <div className="mai-confirm-backdrop" role="presentation" onClick={(e) => { e.stopPropagation(); if (!busy) onCancel(); }}>
      <div className="mai-confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="onb-undo-title"
        aria-describedby="onb-undo-body" onClick={(e) => e.stopPropagation()}>
        <p className="mai-confirm-eyebrow">Ação sensível</p>
        <h2 id="onb-undo-title" className="mai-confirm-title">{title}</h2>
        <p id="onb-undo-body" className="mai-confirm-body">
          Saem as mensagens importadas, os clientes e cards criados pela montagem, e os cards voltam para a coluna em que
          estavam. O que alguém usou depois (cards arrastados ou editados, clientes com conversa nova) fica como está.
        </p>
        {error && <p className="mai-form-error onb-undo-error" role="alert">{error}</p>}
        <div className="mai-confirm-actions">
          <button type="button" className="mai-btn mai-btn-ghost" disabled={busy} onClick={onCancel}>Voltar</button>
          <button ref={confirmRef} type="button" className="mai-btn mai-btn-danger" disabled={busy} onClick={onConfirm}>
            {busy ? 'Aguarde…' : 'Desfazer'}
          </button>
        </div>
      </div>
    </div>
  );
};

const OnboardingHeader: React.FC = () => (
  <header className="mai-page-header">
    <div className="mai-title-row">
      <h1 className="mai-title">Comece por aqui</h1>
    </div>
    <p className="mai-subtitle">Conecte WhatsApp, e-mail e agenda. A MENDS lê o histórico e monta a base sozinha: clientes, conversas e negociações.</p>
  </header>
);

/**
 * Reler conversas para a memória: prévia (sem custo de IA), confirmação e progresso. O backend só grava memória
 * (fatos do cliente); não envia mensagem nem mexe no funil.
 */
const MemoryBackfillBlock: React.FC<{ sectorId: number; index: number }> = ({ sectorId, index }) => {
  const [days, setDays] = useState(30);
  const [job, setJob] = useState<MemoryBackfillJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = useRef(sectorId);
  current.current = sectorId;

  const accept = useCallback((next: MemoryBackfillJob | null, forSector: number) => {
    if (current.current !== forSector) return;
    if (next && next.sectorId !== forSector) return;
    setJob(next);
  }, []);

  // Prévia ou leitura em andamento da conexão: a tela retoma de onde parou.
  useEffect(() => {
    setJob(null);
    setError(null);
    let alive = true;
    fetchActiveMemoryBackfill(sectorId)
      .then((j) => { if (alive) accept(j, sectorId); })
      .catch(() => { if (alive) accept(null, sectorId); });
    return () => { alive = false; };
  }, [sectorId, accept]);

  // Progresso ao vivo enquanto lê.
  const jobId = job?.id;
  const running = job?.status === 'running';
  useEffect(() => {
    if (!jobId || !running) return undefined;
    let alive = true;
    const timer = window.setInterval(() => {
      fetchMemoryBackfill(jobId)
        .then((j) => { if (alive) accept(j, sectorId); })
        .catch(() => undefined);
    }, 3000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [jobId, running, sectorId, accept]);

  const act = async (fn: () => Promise<MemoryBackfillJob | null>) => {
    setBusy(true);
    setError(null);
    try {
      accept(await fn(), sectorId);
    } catch (e) {
      setError(apiErrorMessage(e, 'Não foi possível concluir agora. Tente de novo.'));
    } finally {
      setBusy(false);
    }
  };

  const est = job?.estimate;
  const finished = job?.status === 'done' || job?.status === 'cancelled' || job?.status === 'failed';
  const planned = est?.chunks ?? 0;
  const percent = planned > 0 && job ? Math.min(100, Math.round((job.processedWindows / planned) * 100)) : 0;

  return (
    <WizardStep index={index} title="Reler conversas para a memória" done={job?.status === 'done'} optional>
      <p className="mai-card-detail">
        A MENDS relê as conversas do período e anota na memória de cada cliente o que ele disse (preço, objeção, prazo, decisão).
        É só memória: não envia mensagem e não mexe no funil.
      </p>
      {error && <p className="mai-form-error" role="alert">{error}</p>}

      {(!job || finished) && (
        <div className="onb-row">
          <label htmlFor="onb-memory-window">Período</label>
          <select id="onb-memory-window" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {MEMORY_WINDOWS.map((d) => <option key={d} value={d}>Últimos {d} dias</option>)}
          </select>
          <button type="button" className="mai-btn" disabled={busy}
            onClick={() => act(() => estimateMemoryBackfill(sectorId, days))}>
            {busy ? 'Calculando…' : 'Ver prévia'}
          </button>
        </div>
      )}

      {job?.status === 'estimated' && est && (
        <div className="onb-estimate" aria-label="Prévia da memória">
          <div className="mai-kpi-strip">
            <Kpi label="Conversas" value={fmt(est.conversations)} />
            <Kpi label="Trechos a ler" value={fmt(est.chunks)} />
            <Kpi label="Mensagens" value={fmt(est.messages)} />
            <Kpi label="Áudios e imagens a transcrever" value={fmt(est.mediaToTranscribe)} />
            <Kpi label="Chamadas de IA (estimativa)" value={`~${fmt(est.aiCalls)}`} />
          </div>
          <div className="mai-btn-row">
            <button type="button" className="mai-btn" disabled={busy || est.chunks === 0}
              onClick={() => act(() => confirmMemoryBackfill(job.id))}>Confirmar</button>
            <button type="button" className="mai-btn mai-btn-ghost" disabled={busy}
              onClick={() => act(async () => { await cancelMemoryBackfill(job.id); return null; })}>Cancelar</button>
          </div>
          {est.chunks === 0 && <p className="mai-card-detail onb-note">Não há conversas nesse período.</p>}
        </div>
      )}

      {job && (job.status === 'running' || finished) && (
        <div className="onb-progress" aria-label="Progresso da memória">
          <div className="onb-progress-head">
            <strong>
              {job.status === 'running' && 'Lendo as conversas…'}
              {job.status === 'done' && 'Memória atualizada'}
              {job.status === 'cancelled' && 'Leitura cancelada'}
              {job.status === 'failed' && 'Parou com erro'}
            </strong>
            {job.status === 'running' && planned > 0 && <span className="mai-card-detail">{percent}%</span>}
          </div>
          {job.status === 'running' && planned > 0 && (
            <div className="onb-bar" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
              <span style={{ width: `${percent}%` }} />
            </div>
          )}
          <p className="mai-card-detail">
            {fmt(job.processedWindows)} trechos lidos{planned > 0 ? ` de ~${fmt(planned)}` : ''} · {fmt(job.savedItems)} fatos gravados
          </p>
          {job.status === 'running' && (
            <div className="mai-btn-row">
              <button type="button" className="mai-btn mai-btn-ghost" disabled={busy}
                onClick={() => act(() => cancelMemoryBackfill(job.id))}>Cancelar</button>
            </div>
          )}
        </div>
      )}
    </WizardStep>
  );
};

const Kpi: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="mai-kpi">
    <span className="mai-card-detail">{label}</span>
    <strong>{value}</strong>
  </div>
);

const TabButton: React.FC<{ active: boolean; label: string; onClick: () => void }> = ({ active, label, onClick }) => (
  <button type="button" role="tab" aria-selected={active} className={`onb-tab${active ? ' is-active' : ''}`} onClick={onClick}>{label}</button>
);

const NewConnectionForm: React.FC<{
  name: string;
  official: boolean;
  busy: boolean;
  onName: (v: string) => void;
  onOfficial: (v: boolean) => void;
  onCreate: () => void;
}> = ({ name, official, busy, onName, onOfficial, onCreate }) => (
  <div className="onb-new">
    <label htmlFor="onb-new-name">Nome da conexão</label>
    <input id="onb-new-name" value={name} placeholder="Ex.: Comercial" onChange={(e) => onName(e.target.value)} />
    <div className="onb-radio" role="radiogroup" aria-label="Tipo de WhatsApp">
      <label><input type="radio" checked={!official} onChange={() => onOfficial(false)} /> WhatsApp pelo QR code (o número que você já usa)</label>
      <label><input type="radio" checked={official} onChange={() => onOfficial(true)} /> WhatsApp oficial (Meta)</label>
    </div>
    <button type="button" className="mai-btn" onClick={onCreate} disabled={busy}>{busy ? 'Criando…' : 'Criar conexão'}</button>
  </div>
);

export default OnboardingPage;
